import { cn } from '@client/utils';
import { Tooltip } from 'antd';
import { Cpu } from 'lucide-react';

type Size = 'x-small' | 'small' | 'medium' | 'large';

/** Outer circle diameter (px) — mirrors the avatar sizes in {@link User}. */
const SIZES = {
    'x-small': 14,
    small: 16,
    medium: 22,
    large: 28,
} as const satisfies Record<Size, number>;

/** Inner glyph size, scaled to sit comfortably inside the circle. */
const ICON_SIZES = {
    'x-small': 'h-2 w-2',
    small: 'h-2.5 w-2.5',
    medium: 'h-3 w-3',
    large: 'h-3.5 w-3.5',
} as const satisfies Record<Size, string>;

type Props = {
    /** Optional secondary label (e.g. service token name); rarely needed. */
    note?: string;
    /** Matches the {@link User} size scale so system rows align with user rows. */
    size?: Size;
    /** Render as `inline-flex` so the chip sits in a line of text without breaking baseline. */
    inline?: boolean;
    /** Show the system glyph. Set `false` for name-only contexts (mirrors {@link User}). */
    showAvatar?: boolean;
    /** Show the "System" label. Set `false` for avatar-only contexts (mirrors {@link User}). */
    showName?: boolean;
    /** Extra classes merged onto the outer wrapper (e.g. `cursor-pointer`). */
    className?: string;
    /** Granular class overrides for the glyph and label (mirrors {@link User}). */
    classNames?: {
        avatar?: string;
        name?: string;
    };
};

/**
 * Renders the platform-system actor — used wherever an entity was created or
 * acted on by the platform itself rather than a person (`userId` equal to the
 * `SYSTEM_ACCOUNT_IDS.SYSTEM` sentinel, or `EntityHistory` rows with a null `userId`).
 * Visually distinct from a real user avatar so operators can tell automated
 * actions apart at a glance. {@link User} delegates here for the system sentinel
 * (forwarding its presentational props) so the treatment is consistent across
 * every "created by"/actor display.
 */
export function SystemActor({
    note,
    size = 'medium',
    inline = false,
    showAvatar = true,
    showName = true,
    className,
    classNames,
}: Props) {
    const diameter = SIZES[size];

    return (
        <Tooltip title="Automated platform action — no human actor">
            <span className={cn('items-center gap-1', inline ? 'inline-flex' : 'flex', className)}>
                {showAvatar && (
                    <span
                        className={cn('flex items-center justify-center rounded-full bg-muted', classNames?.avatar)}
                        style={{ height: diameter, width: diameter, minWidth: diameter }}
                    >
                        <Cpu className={cn(ICON_SIZES[size], 'text-muted-foreground')} />
                    </span>
                )}
                {showName && (
                    <span
                        className={cn('font-medium text-foreground', size === 'large' && 'text-base', classNames?.name)}
                    >
                        System
                    </span>
                )}
                {note ? <span className="text-muted-foreground">({note})</span> : null}
            </span>
        </Tooltip>
    );
}
